import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/ar';
import 'dayjs/locale/bn';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import 'dayjs/locale/ht';
import 'dayjs/locale/ko';
import 'dayjs/locale/ru';
import 'dayjs/locale/ur';
import 'dayjs/locale/zh';
import { ServiceHour } from '../types';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);

export default {
  dateAndTimeToDayjs,
  secondsSinceMidnightIntoDate,
  timeSinceMidnightToString,
  friendlyDateTime,
  dayFromServiceDate,
  getServiceRow,
  getBriefServiceRow,
  getServiceDateFromDate,
  getSeccondsSinceMidnightFromUnixTimestamp,
  getServiceDateFromUnixTimestamp,
  getNiceDate,
  getWeekDay,
  getShortDate,
  getDateYear,
  getShortDateFromUnixTimestamp,
  getMonthLabel,
  getDaysInMonth,
  formatBirthdate,
  formatDate,
  weekdaysToDayNames,
};

function dateAndTimeToDayjs(date: string, time: number) {
  if (!time) {
    time = 0;
  }
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  return dayjs(`${date} ${hours}:${minutes < 10 ? '0' : ''}${minutes}`, 'YYYY-MM-DD h:mm A');
}

function secondsSinceMidnightIntoDate(time: number) {
  // use 2020-06-01 to ensure it works ok on days of daylight savings
  let midnight = dayjs(new Date('2020-06-01')).startOf('day').unix();
  let dateTime = midnight + time;
  return new Date(dateTime * 1000);
}

function getSeccondsSinceMidnightFromUnixTimestamp(timestamp: number) {
  let midnight = dayjs.unix(timestamp).startOf('day').unix();
  return timestamp - midnight;
}

function timeSinceMidnightToString(time: number) {
  let date = secondsSinceMidnightIntoDate(time);
  return dayjs(date).format('h:mm a');
}

function friendlyDateTime(date: string) {
  return dayjs(date).format('YYYY-MM-DD h:mm A');
}

function dayFromServiceDate(serviceDate: string) {
  return dayjs(serviceDate).format('ddd, M/D');
}

function getServiceRow(service: ServiceHour, translation: Record<string, string>) {
  const from = service.from || service.time;
  if (!service.date || !from) {
    return '';
  }
  const start = timeSinceMidnightToString(from).replace(':00', '');
  const end = timeSinceMidnightToString(service.until).replace(':00', '');
  const day = dayFromServiceDate(service.date);
  let restrictions = '';
  if (service.service_restriction && service.service_restriction !== 'none') {
    const key = `service_restriction_${service.service_restriction}`;
    restrictions = ` (${translation[key]})`;
  }
  return `${day} ${start} - ${end}${restrictions}`;
}

function getBriefServiceRow(service: ServiceHour) {
  const from = service.from || service.time;
  if (!from) {
    return '';
  }
  const start = timeSinceMidnightToString(from).replace(':00', '');
  const end = timeSinceMidnightToString(service.until).replace(':00', '');
  const day = dayjs(service.date).format('M/D');
  return `${day} ${start} - ${end}`;
}

function getServiceDateFromDate(date: string) {
  return dayjs(date).format('YYYY-MM-DD');
}

function getServiceDateFromUnixTimestamp(timestamp: number) {
  return dayjs.unix(timestamp).utc().format('YYYY-MM-DD');
}

function getNiceDate(serviceDate: string, locale: string, todayString: string) {
  const today = dayjs(new Date()).format('YYYY-MM-DD');
  if (today === serviceDate) {
    return `${todayString}, ${dayjs(serviceDate).locale(locale).format('MMMM D, YYYY')}`;
  }
  return dayjs(serviceDate).locale(locale).format('dddd, MMMM D, YYYY');
}

function getWeekDay(serviceDate: number, locale: string) {
  return dayjs(serviceDate).locale(locale).format('dddd');
}

function weekdaysToDayNames(dayIndexes: Array<number>) {
  return dayIndexes.map((index) => dayjs().startOf('week').add(index, 'day').format('dddd'));
}

function getShortDate(serviceDate: string, locale: string, todayString: string) {
  const today = dayjs(new Date()).format('YYYY-MM-DD');
  if (today === serviceDate) {
    return todayString;
  }

  return dayjs(serviceDate).locale(locale).format('ddd, MMM D');
}

function getDateYear(serviceDate: number, locale: string) {
  return dayjs(serviceDate).locale(locale).format('ddd MMM D, YYYY');
}

function getShortDateFromUnixTimestamp(timestamp: number, locale: string) {
  const now = Math.floor(Date.now() / 1000);
  if (now - timestamp > 365 * 24 * 3600) {
    return dayjs.unix(timestamp).locale(locale).format('MMM D, YY');
  } else if (now - timestamp > 24 * 3600) {
    return dayjs.unix(timestamp).locale(locale).format('MMM D');
  } else {
    return dayjs.unix(timestamp).locale(locale).format('h:mm A');
  }
}

function getMonthLabel(month: number, locale: string) {
  return dayjs().month(month).locale(locale).format('MMM');
}

function getDaysInMonth(year: string, month: string) {
  const iMonth = parseInt(month, 10);
  const decMonth = iMonth > 9 ? iMonth : `0${iMonth}`;
  const date = `${year}-${decMonth}`;
  return dayjs(date, 'YYYY-MM').daysInMonth();
}

function formatDate(date: string) {
  const returnDate = dayjs(date).format('LL');
  if (returnDate == 'Invalid date') {
    return '-';
  }

  return returnDate;
}

function formatBirthdate(value: string | number | Date) {
  if (Object.prototype.toString.call(value) === '[object Date]') {
    value = dayjs(value).format('MM/DD/YYYY');
  } else if (typeof value === 'number') {
    value = dayjs.unix(value).utc().format('MM/DD/YYYY');
  }

  if (typeof value === 'string') {
    value = value
      .replace(/^(\d\d)(\d)$/g, '$1/$2')
      .replace(/^(\d\d\/\d\d)(\d+)$/g, '$1/$2')
      .replace(/[^\d\/]/g, '');
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
  }

  return value;
}
